import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { GET_AD } from '../../graphql/queries';
import { TOGGLE_FAVORITE } from '../../graphql/mutations';
import { useAuth } from '../../context/AuthContext';

const MobileAdDetail: React.FC = () => {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  // The slug might be partially encoded, so we need to handle it carefully
  let slug = rawSlug || '';
  
  // Try to decode if it looks encoded
  try {
    if (slug.includes('%')) {
      slug = decodeURIComponent(slug);
    }
  } catch (e) {
    // If decoding fails, use as-is
  }
  
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { data, loading, error } = useQuery(GET_AD, {
    variables: { slug },
    skip: !slug,
  });

  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE, {
    refetchQueries: [{ query: GET_AD, variables: { slug } }],
  });

  const ad = data?.adBySlug;

  useEffect(() => {
    if (ad?.title) document.title = ad.title;
    return () => { document.title = 'NayaBajar'; };
  }, [ad?.title]);

  // Format price to avoid decimals
  const formatPrice = (price: string | null | undefined) => {
    if (!price) return 'Free';
    // If it's a string that looks like a number with decimals, format it
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      // Remove decimals and format with commas
      return Math.round(numPrice).toLocaleString('en-NP');
    }
    return price;
  };

  if (loading) {
    return (
      <MobileLayout showHeader={false}>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </MobileLayout>
    );
  }

  if (error) {
    return (
      <MobileLayout showHeader={false}>
        <div className="text-center py-12 px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Ad</h2>
          <p className="text-gray-600 text-sm mb-4">There was a problem loading this ad.</p>
          <button onClick={() => navigate(-1)} className="px-6 py-2.5 bg-blue-500 text-white rounded-full font-medium">
            Go Back
          </button>
        </div>
      </MobileLayout>
    );
  }

  if (!ad) {
    return (
      <MobileLayout showHeader={false}>
        <div className="text-center py-12 px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ad not found</h2>
          <p className="text-gray-600 text-sm mb-4">This ad may have been removed.</p>
          <button onClick={() => navigate(-1)} className="px-6 py-2.5 bg-blue-500 text-white rounded-full font-medium">
            Go Back
          </button>
        </div>
      </MobileLayout>
    );
  }

  const images = ad.imagesList || [];
  const hasImages = images.length > 0;

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      await toggleFavorite({ variables: { ad_id: ad.id } });
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  return (
    <MobileLayout showHeader={false} showBottomNav={false}>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg"
      >
        <svg
          className="w-6 h-6 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      {/* Favorite Button */}
      <button
        onClick={handleToggleFavorite}
        className="fixed top-4 right-4 z-50 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg"
      >
        <svg
          className={`w-6 h-6 ${ad.isFavorited ? 'text-red-500 fill-current' : 'text-gray-700'}`}
          fill={ad.isFavorited ? 'currentColor' : 'none'}
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      </button>

      {/* Image Gallery */}
      <div className="relative bg-black">
        {hasImages ? (
          <>
            <motion.img
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              src={images[currentImageIndex].url}
              alt={ad.title}
              className="w-full h-80 object-cover"
            />
            {images.length > 1 && (
              <>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_: any, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`h-1.5 rounded-full transition-all ${
                        idx === currentImageIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
                {currentImageIndex > 0 && (
                  <button
                    onClick={() => setCurrentImageIndex((prev) => prev - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                {currentImageIndex < images.length - 1 && (
                  <button
                    onClick={() => setCurrentImageIndex((prev) => prev + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <div className="w-full h-80 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <svg
              className="w-24 h-24 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-t-3xl -mt-6 relative z-10 px-4 pt-6 pb-4">
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-3xl font-bold text-gray-900">Rs {formatPrice(ad.price)}</span>
          {ad.priceType === 'negotiable' && (
            <span className="text-sm text-gray-500">(Negotiable)</span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 mb-3">{ad.title}</h1>

        {/* Location */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{ad.location || ad.city}</span>
        </div>

        {/* Details */}
        {(ad.condition || ad.brand || ad.modelName || ad.year) && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Details</h2>
            <div className="grid grid-cols-2 gap-3">
              {ad.condition && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Condition</p>
                  <p className="font-medium text-gray-900 capitalize">{ad.condition}</p>
                </div>
              )}
              {ad.brand && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Brand</p>
                  <p className="font-medium text-gray-900">{ad.brand}</p>
                </div>
              )}
              {ad.modelName && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Model</p>
                  <p className="font-medium text-gray-900">{ad.modelName}</p>
                </div>
              )}
              {ad.year && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Year</p>
                  <p className="font-medium text-gray-900">{ad.year}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seller Info */}
        <div className="mb-4 flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <img
            src={ad.user.profilePicture || '/default-avatar.png'}
            alt={ad.user.displayName}
            className="w-12 h-12 rounded-full object-cover border-2 border-gray-100 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-semibold text-gray-900 truncate">{ad.user.displayName}</span>
              {ad.user.isVerified && (
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Member since {ad.user.dateJoined ? new Date(ad.user.dateJoined).getFullYear() : 'N/A'}
            </p>
          </div>
          <button
            onClick={() => navigate(`/profile/${ad.user.username}`)}
            className="text-blue-500 text-sm font-medium flex-shrink-0"
          >
            View Profile
          </button>
        </div>

        {/* Contact Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => navigate('/messages')}
            className="flex-1 py-2.5 bg-blue-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Chat
          </button>
          {ad.contactWhatsapp && (
            <a
              href={`https://wa.me/${ad.contactWhatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 bg-green-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.134.558 4.133 1.533 5.868L0 24l6.302-1.51A11.946 11.946 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.372l-.36-.213-3.733.894.938-3.63-.234-.373A9.818 9.818 0 1112 21.818z"/>
              </svg>
              WhatsApp
            </a>
          )}
          {ad.contactPhone && (
            <a
              href={`tel:${ad.contactPhone}`}
              className="flex-1 py-2.5 bg-gray-800 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call
            </a>
          )}
        </div>

        {/* Description */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
            {ad.description || 'No description provided.'}
          </p>
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileAdDetail;
